import { ReactNode } from "react";
import { SP } from "../http/serviceportalApi";
import { VV } from "../http/vertragsverwaltungApi";
import { Permission } from "../types";

const i18n = {
  title: "HGV Serviceportal",
  loading: "Lade …",
  notAvailable: "N / V",
  footer: {
    companyName: "Hoteliers- und Gastwirteverband (HGV)",
    street: "Schlachthofstraße 59",
    city: "39100 Bozen",
  },
  notifications: {
    saveSuccess: "Daten erfolgreich gespeichert.",
    saveError: "Fehler beim Speichern der Daten.",
  },
  appOpenError: "Fehler beim Öffnen der App.",
  home: {
    title: "Willkommen im HGV Serviceportal",
    description:
      "Hier finden Kunden der HGV-Dienstleistungsabteilungen exklusive Serviceangebote & Dienstleistungen. Zudem können Sie rund um die Uhr Lohnauswertungen abrufen, individuelle Arbeitsverträge ausdrucken, den Umsatz und die Kostenentwicklung analysieren und alle Leistungskennzahlen Ihres Betriebes einsehen und vieles mehr.",
    recentApps: "Ihre zuletzt benutzten Anwendungen",
    recentAppsEmpty:
      "Hier werden Sie die Anwendungen finden, die Sie kürzlich verwendet haben.",
  },
  accountButton: {
    account: "Konto",
    invites: "Einladungen",
    logout: "Abmelden",
    language: "Sprache",
  },
  menu: {
    clientPortal: "Portal für Kunden",
    economicPortal: "Portal für Wirtschaftsberater",
    privatePortal: "Portal für Privathaushalte",
    auditPortal: "Portal für Revisoren",
    admin: "Administration",
  },
  invites: {
    title: "Einladungen",
    columnHeaders: {
      member: "Mitglied",
      companies: "Betriebsstätten",
      validUntil: "Gültig bis",
      email: "E-Mail-Adresse",
      status: "Status",
    },
    accept: "Einladung annehmen",
    decline: "Einladung ablehnen",
    status: {
      declined: "Abgelehnt",
      pending: "Ausstehend",
      expired: "Abgelaufen",
    },
  },
  nav: {
    home: "Startseite",
    myBusiness: "Mein Betrieb",
    services: "Dienstleistungen",
    quickAccess: "Schnellzugriff",
    apps: "Anwendungen",
  },
  joinFutureHospitality: {
    title: "Warum Mitarbeitende deine neuen Gäste sind",
    paragraph1:
      "Mit JOIN future hospitality machen wir Südtirol gemeinsam zur Top-Destination für Gastronomie- und Hotellerie-Talente! Bereite dich vor und starte jetzt den Readiness-Check – dein erster Schritt in Richtung Future Hospitality.",
    paragraph2: "Schau nach, wo du als Arbeitgeber stehst.",
    notInterested: "Kein Interesse",
    readinessCheck: "Zum Readiness-Check",
  },
  memberSelect: {
    loadError: "Fehler beim Laden des Mitglieds",
    searchPlaceholder: "Suche Mitglieder …",
    groupTitle: "Mitglieder",
    noResults: (query: string) => (
      <>
        Es wurden keine mit Ihrer Suchanfrage – <strong>{query}</strong> –
        übereinstimmenden Mitglieder gefunden.
      </>
    ),
    empty: "Keine Mitglieder gefunden.",
    loadMembersError: "Fehler beim Laden der Mitglieder",
  },
  errorPage: {
    apology: "Entschuldigung",
    unexpectedError:
      "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es in Kürze erneut.",
    contactHgv:
      "Sollte der Fehler weiterhin bestehen, wenden Sie sich bitte an Ihren HGV-Ansprechpartner.",
    altTextError: "Fehler",
  },
  table: {
    outOf: "von",
    defaultSearchPlaceholder: "Suche",
    noItems: "Keine Einträge gefunden.",
    loadError: "Fehler beim Laden der Einträge",
    prevPage: (pageSize: number) => `Vorherige ${pageSize} Zeilen`,
    nextPage: (pageSize: number) => `Nächste ${pageSize} Zeilen`,
  },
  formField: {
    save: "Speichern",
    optional: "(optional)",
    errors: {
      required: "Das Feld muss einen Wert beinhalten.",
      min: "Das Feld enthält keinen gültigen Wert, geben Sie einen größeren Wert ein.",
      max: "Das Feld enthält keinen gültigen Wert, geben Sie einen kleineren Wert ein.",
      pattern: "Der Wert hat ein ungültiges Format.",
      validate: "Das Feld enthält keinen gültigen Wert.",
    },
  },
  departments: {
    accounting: {
      title: "Steuerberatung",
      description:
        "Die HGV-Steuerberatung ist Ihr Experte für das Steuerrecht im Gastgewerbe. Kompetent, zuverlässig und professionell erledigen wir für Sie alle anfallenden Aufgaben rund um die Steuern.",
      apps: {
        tagesinkasso: {
          title: "Tagesinkassobuch",
          description:
            "Dient der vorgeschriebenen Erfassung der täglichen Tagesinkassi und bei ordentlicher Buchhaltung zur Führung des Kassabuches (Bargeldbewegungen).",
        },
        monatsdaten: {
          title: "Monatsdaten",
          description:
            "Dient zur monatlichen Erfassung der betrieblichen Strukturdaten, wie Bettenanzahl, Nächtigungen, Sitzplätze, Öffnungstage.",
        },
        bilanzOnline: {
          title: "Kennzahlen",
          description:
            "Dient zur Einsicht steuerlicher und zivilrechtlicher Kennzahlen.",
        },
        businessCheck: {
          title: "Business-Check",
          description:
            "Dient zur laufenden Überwachung der Betriebsentwicklung mittels aktueller Bilanzdaten, Grafiken und Jahresvergleiche.",
        },
        openItems: {
          title: "Offene Posten",
          description:
            "Dient zur aktualisierten Übersicht aller offenen Posten.",
        },
        invoicePortal: {
          title: "Rechnungsportal",
          description:
            "Dient zur einfachen Erstellung und Verwaltung aller Eingangs- und Ausgangsrechnungen.",
        },
        endbestaende: {
          title: "Endbestände",
          description:
            "Detaillierte Erfassung der Endbestände von Waren und Gütern zum Jahresende.",
        },
      },
    },
    joinHospitality: {
      title: "JOIN hospitality",
      description:
        "JOIN hospitality hilft Ihrem Betrieb, sich als attraktiver Arbeitgeber zu positionieren, und stärkt so Ihre Suche und Bindung von Mitarbeitenden. Wir begleiten HGV-Mitgliedsbetriebe dabei mit kostenlosem Readiness-Check und Erstcoaching, individueller Beratung und konkreten Werkzeugen für Organisation, Führung und Mitarbeitergewinnung.",
      backLink: "Zurück zu JOIN hospitality",
      apps: {
        readinessCheck: {
          title: "Readiness-Check",
          description:
            "Wie attraktiv sind Sie als Arbeitgeber wirklich? Der kostenlose Readiness-Check zeigt, wo Sie stehen, und liefert konkrete Ansatzpunkte und Handlungsempfehlungen für die Weiterentwicklung Ihres Betriebes.",
        },
        joinToolbox: {
          title: "JOIN Toolbox",
          description:
            "Mitarbeitende gewinnen, einarbeiten und halten: Hier finden Sie Informationen, Checklisten und Leitfäden für jede Phase der Zusammenarbeit, von der Suche bis zur langfristigen Bindung.",
        },
        joinJobOffers: {
          title: "Offene Stellen auf JOIN-Website anzeigen",
          description:
            "Zeigen Sie Ihre offenen Stellen direkt auf der JOIN-hospitality-Website. Hier finden Sie eine Schritt-für-Schritt-Anleitung zur Veröffentlichung.",
        },
        continuingEducation: {
          title: "Bereich Weiterbildung im HGV",
          description:
            "Der Bereich Weiterbildung bietet Hotelierinnen, Hoteliers, Gastwirtinnen und Gastwirten sowie deren Mitarbeitenden ein umfangreiches Weiterbildungsprogramm.",
          url: "https://www.hgv.it/de/dienstleistungen/weiterbildung",
        },
        consultingAndCoaching: {
          title: "Beratung & Coaching",
          description:
            "Wir machen Sie fit im Mitarbeitermanagement. Mit unseren HR-Beratungsangeboten gewinnen und binden Sie hervorragende Mitarbeitende – und entwickeln durch unser Arbeitgeber-Coaching Ihre Führungskompetenzen stetig weiter.",
        },
      },
    },
    payroll: {
      title: "Personalberatung",
      description:
        "Die HGV-Personalberatung ist Ihr zuverlässiger und kompetenter Ansprechpartner, wenn es um Ihre Mitarbeiter:innen geht. Hier können Sie schnell und unkompliziert auf sämtliche Dokumente und Anwendungen zur Verwaltung Ihres Personals zugreifen.",
      apps: {
        arbeitssicherheit: {
          title: "Arbeitssicherheit für Arbeitnehmer:innen",
          description:
            "Anmeldung zu dem gesetzlich vorgeschriebenen Arbeitssicherheitskurs für Mitarbeiter:innen.",
        },
        hourRegistration: {
          title: "Stundenregister",
          description:
            "Einfache Eingabe und Verwaltung der Arbeitsstunden der Mitarbeiter. Kein Ausdrucken und Ablegen der Stundenregister mehr.",
        },
        employeeRegistration: {
          title: "Mitarbeitermeldung",
          description: (
            <>
              Um die termingerechte Meldung an das Arbeitsamt sowie die
              Bereitstellung des Arbeitsvertrages garantieren zu können, werden
              die vollständigen Informationen{" "}
              <strong>bis spätestens 11:45 Uhr</strong> des letzten Werktages
              vor Arbeitsbeginn benötigt.
            </>
          ),
        },
        urgentEmployeeRegistration: {
          title: "Dringende Mitarbeiteranmeldung",
          description:
            "Dringende Mitarbeiteranmeldung außerhalb der Öffnungszeiten.",
        },
        emergencyService: {
          title: "Notfalldienst",
          description:
            "Häufige Problemfälle mit Fragen und Antworten (FAQ). Falls die angegebenen FAQs Ihren konkreten Problemfall nicht lösen, kann ein individueller Notfalldienst aktiviert werden.",
        },
        arbeitAufAbruf: {
          title: "Arbeit auf Abruf – Vorabmeldung des Abrufs",
          description:
            "Für jede:n Beschäftigte:n, welcher mit dem Vertrag „Arbeit auf Abruf“ eingestellt ist, muss jeweils vor Arbeitsbeginn eine zusätzliche Mitteilung an das Arbeitsministerium erfolgen.",
        },
      },
    },
    webMarketing: {
      title: "Web Marketing",
      description: (link: ReactNode) => (
        <>
          Die Abteilung Web Marketing ist in der Kundenberatung rund um das
          Thema Online-Marketing / Online-Buchbarkeit tätig und mit der
          Entwicklung diverser Tools und Portale stets darum bemüht, Ihnen die
          Betriebsorganisation zu erleichtern und Sie bei Ihrem Auftritt im Web
          optimal zu unterstützen. Einen Überblick über unsere Produkte und
          Dienstleistungen finden Sie auf unserer {link}.
        </>
      ),
      descriptionLinkLabel: "Demoseite",
      apps: {
        dac7: {
          title: "DAC7-Meldepflicht",
          description: (
            <>
              Aufgrund des legislativen Dekrets Nr. 32/2023, welches die
              EU-Richtlinie „DAC7“ in Italien umsetzt, sind digitale
              Plattformbetreiber dazu verpflichtet, europäischen Steuerbehörden
              Daten und Transaktionen der registrierten Verkäufer offenzulegen.
            </>
          ),
        },
      },
    },
  },
  services: {
    open: "Öffnen",
    new: "Neu",
  },
  allCompanies: "Alle Betriebsstätten",
  companies: (count: number) =>
    `${count} ${count === 1 ? "Betriebsstätte" : "Betriebsstätten"}`,
  quickAccessTypes: {
    documents: "Dokumente",
  },
  documentSet: {
    lohnauswertung: "Lohnauswertung",
    "zahlungsbestaetigung-f24": "Zahlungsbestätigung F24",
    einheitslohnbuch: "Einheitslohnbuch",
    "formblatt-770": "Formblatt 770",
    "formblatt-cu": "Formblatt CU",
    matrikelbuch: "Matrikelbuch",
    arbeitsvertraege: "Arbeitsverträge",
    "lohn-rundschreiben": "Rundschreiben",
    "steuer-rundschreiben": "Rundschreiben",
    "prov-lohnstreifen": "Prov. Lohnstreifen",
    "zusaetzliche-auswertungen": "Zusätzliche Auswertungen",
    jahresdokumente: "Jahresdokumente",
    "antrag-um-familiengeld": "Antrag um Familiengeld",
    jahresauswertungen: "Jahresauswertungen",
  },
  getAgreementStatus: (status: VV.AgreementStatus): string => {
    switch (status) {
      case "Published":
        return "Offen";
      case "Signed":
        return "Bestätigt";
      case "Cancelled":
        return "Gekündigt";
      default:
        return i18n.notAvailable;
    }
  },
  getPermission: (p: Permission): string => {
    switch (p) {
      case Permission.UsersRead:
        return "Benutzer lesen";
      case Permission.UsersWrite:
        return "Benutzer schreiben";
      case Permission.InvitesRead:
        return "Einladungen lesen";
      case Permission.MemberUsersRead:
        return "Mitgliedsbenutzer lesen";
      case Permission.MemberUsersWrite:
        return "Mitgliedsbenutzer schreiben";
      case Permission.AccountingsRead:
        return "Kennzahlen";
      case Permission.OpenItemsRead:
        return "Offene Posten";
      case Permission.WageCostsRead:
        return "Kennzahlen";
      case Permission.HourRegistrationExecute:
        return "Stundenregister";
      case Permission.EmployeeRegistrationExecute:
        return "Mitarbeitermeldung";
      case Permission.InvoicePortalExecute:
        return "SEAC-Rechnungsportal";
      case Permission.AgreementsExecute:
        return "Vertragsverwaltung";
      case Permission.BusinessCheckExecute:
        return "Business-Check";
      case Permission.TIBuchRead:
        return "Tagesinkassobuch";
      case Permission.MonatsdatenDataWrite:
        return "Monatsdaten";
      case Permission.MonatsdatenSettingsWrite:
        return "Monatsdaten-Admin";
      case Permission.EndbestaendeWorksheetsWrite:
        return "Endbestände";
      case Permission.EndbestaendeSettingsWrite:
        return "Endbestände-Admin";
      case Permission.JoinHospitalityLevel1Read:
        return "JOIN hospitality Level 1";
      case Permission.JoinHospitalityLevel2Read:
        return "JOIN hospitality Level 2";
      case Permission.Dac7Write:
        return "DAC7-Meldepflicht";
      case Permission.DocumentsSteuerRundschreibenRead:
        return "Rundschreiben";
      case Permission.DocumentsJahresauswertungRead:
        return "Jahresauswertung";
      case Permission.DocumentsRevisorenRead:
        return "Revisorendokumente";
      case Permission.DocumentsBetriebOnlineServiceRead:
        return "Betrieb Online Service";
      case Permission.DocumentsBetriebStandardRead:
        return "Betrieb Standard";
      case Permission.DocumentsPrivateHaushalteRead:
        return "Private Haushalte";
      case Permission.DocumentsWirtschaftsberaterRead:
        return "Wirtschaftsberater";
      case Permission.DocumentsAufstellungNettoloehneRead:
        return "Aufstellung Nettolöhne";
      case Permission.DocumentsBuchungsbelegeRead:
        return "Buchungsbelege";
      case Permission.DocumentsCUDRead:
        return "CUD";
      case Permission.DocumentsEinbehalt15Read:
        return "Einbehalt 1/5";
      case Permission.DocumentsErklSteuerfreibetraegeRead:
        return "Erkl. Steuerfreibeträge";
      case Permission.DocumentsLaborfondMonatlichRead:
        return "Laborfonds monatlich";
      case Permission.DocumentsLaborfondTrimestralRead:
        return "Laborfonds vierteljährlich";
      case Permission.DocumentsLohnstreifenRead:
        return "Lohnstreifen";
      case Permission.DocumentsMatrikelbuchRead:
        return "Matrikelbuch";
      case Permission.DocumentsF24Read:
        return "F24";
      case Permission.DocumentsNummInailRead:
        return "Numm. INAIL";
      case Permission.DocumentsSchulgeldLehrlingeRead:
        return "Schulgeld Lehrlinge";
      case Permission.DocumentsUeberweisungslisteRead:
        return "Überweisungsliste";
      case Permission.DocumentsZahlungsbestaetigungF24Read:
        return "Zahlungsbestätigung F24";
      case Permission.DocumentsEinheitslohnbuchRead:
        return "Einheitslohnbuch";
      case Permission.Documents770SemplificatoRead:
        return "770 semplificato";
      case Permission.Documents770OrdinarioRead:
        return "770 ordinario";
      case Permission.DocumentsArbeitsvertraegeRead:
        return "Arbeitsverträge";
      case Permission.DocumentsLohnRundschreibenRead:
        return "Rundschreiben";
      case Permission.DocumentsProvisorischerLohnstreifenRead:
        return "Provisorischer Lohnstreifen";
    }
  },
  getDepartment: (d: SP.Department): string => {
    switch (d) {
      case "Steuer":
        return "Steuerberatung";
      case "Personal":
        return "Personalberatung";
      default:
        return d;
    }
  },
  admin: {
    title: "Serviceportal Administration",
    users: {
      title: "Benutzer",
      searchPlaceholder: "Suche Benutzer",
      columnHeaders: {
        name: "Name",
      },
      deleteModal: {
        title: "Sind Sie sich sicher?",
        cancel: "Abbrechen",
        confirm: "Entfernen",
        body: (email: string) => (
          <>
            Wollen Sie <strong>{email}</strong> wirklich entfernen?
          </>
        ),
      },
    },
    user: {
      backLink: "Zurück zu Benutzer",
      id: "ID",
      tabs: {
        infos: "Infos",
        assignments: "Zugriff",
      },
      loadError: "Fehler beim Laden des Benutzers.",
      infos: {
        name: "Name",
        email: "E-Mail-Adresse",
      },
    },
    invites: {
      title: "Einladungen",
      columnHeaders: {
        email: "E-Mail-Adresse",
        member: "Mitglied",
        companies: "Betriebsstätten",
        validUntil: "Gültig bis",
        status: "Status",
      },
    },
    assignments: {
      columnHeaders: {
        member: "Mitglied",
        companies: "Betriebsstätten",
        company: "Betriebsstätte",
        access: "Zugriff",
      },
    },
    accounting: {
      title: "Mitgliedsverwaltung",
      label: {
        company: "Betriebsstätte",
        endDate: "Enddatum",
        member: "Unternehmen",
        searchMemberPlaceholder: "Suche Unternehmen",
        status: "Status",
        year: "Jahr",
      },
      worksheet: {
        status: {
          completed: "Abgeschlossen",
          in_progress: "In Bearbeitung",
          not_started: "Nicht begonnen",
        },
      },
    },
  },
  revisorApp: {
    title: "Serviceportal Revisoren",
  },
  economicAdvisorApp: {
    title: "Serviceportal Wirtschaftsberater",
    questionnaire: {
      title: "Modell CU / 770",
      description: {
        paragraph1:
          "Im Folgenden finden Sie die Liste unserer gemeinsamen Kunden.",
        paragraph2: (
          <>
            Bei den Kunden, für die Sie den Versand der Certificazione Unica
            (CU) betreffend Freiberufler durchgeführt haben, aktivieren Sie
            bitte das Kontrollkästchen. Bitte geben Sie die Steuernummer an, mit
            der Sie den Versand des Mod. 770 vornehmen werden.{" "}
            <strong>
              Mit der Aktivierung übernehmen Sie gleichzeitig die Verantwortung
              für den termingerechten Versand des Mod. 770 betreffend
              Freiberufler.
            </strong>
          </>
        ),
        paragraph3:
          "Der telematische Versand des Mod. 770 (betreffend Arbeitnehmende) erfolgt von Seiten der HGV-Service-Genossenschaft mit folgender Steuernummer: 00576540215.",
        paragraph4:
          "Sofern für die Kunden ein Revisor tätig ist, bitten wir Sie, auch dessen Daten anzugeben.",
      },
      disabled:
        "Die Eingabe ist nicht mehr möglich. Bitte wenden Sie sich an Ihre HGV-Personalberatung:",
      deadlineAlert: (deadline: string) =>
        `Bitte bestätigen Sie die Eingabe innerhalb ${deadline}, indem Sie auf „Bestätigen“ klicken, auch wenn Sie keine Kontrollkästchen aktiviert haben.`,
      noCustomers: "Keine Kundenliste vorhanden.",
      submit: "Bestätigen",
      loadError: "Fehler beim Laden der Daten.",
      columnHeaders: {
        name: "Name",
        freelanceInvoice: "Freiberuflerrechnung",
        revisor: "Revisor",
      },
      freelanceTaxCode: {
        label: "Freiberufler-Steuernummer",
        helpText:
          "Steuernummer, mit welcher der Versand 770 – Freiberufler bzw. Capitali durchgeführt wird.",
        invalidError: "Ungültige Steuernummer.",
      },
      revisorFunction: {
        label: "Revisorfunktion",
        placeholder: "— Bitte wählen —",
      },
      revisorTaxCode: "Steuernummer",
      revisorTypes: {
        revisore_contabile: {
          label: "Revisore contabile",
        },
        societa_e_responsabile: {
          label: "Società di revisione & Responsabile per la revisione",
          helpTexts: {
            societa: "Società di revisione",
            responsabile: "Responsabile per la revisione",
          },
        },
        collegio_sindacale: {
          label: "Collegio sindacale",
        },
      },
    },
  },
  privateHouseholdApp: {
    title: "Serviceportal Privathaushalte",
  },
  masterData: {
    title: "Stammdaten",
    description:
      "Ihre Mitgliedsdaten und allgemeine Informationen über Ihre Betriebsstätten.",
    name: "Name",
    street: "Straße",
    city: "Ort",
    postalCode: "PLZ",
    memberData: {
      title: "Mitgliedsdaten",
      memberId: "Mitgliedsnummer",
      taxId: "Steuernummer",
      vatId: "MwSt.-Nummer",
    },
    companies: {
      title: "Betriebsstätten",
      companyId: "Betriebsstättennummer",
    },
  },
  memberSettings: {
    title: "Benutzer",
    description:
      "Hier finden Sie eine Auflistung der Benutzer, die Einsicht in die Daten einer oder mehrerer Ihrer Betriebsstätten haben.",
    inviteUser: "Benutzer einladen",
    tabs: {
      users: "Benutzer",
      invites: "Einladungen",
    },
    users: {
      columnHeaders: {
        name: "Name",
        email: "E-Mail-Adresse",
        companies: "Betriebsstätten",
        company: "Betriebsstätte",
        access: "Zugriff",
      },
      legalRepresentative: "Gesetzlicher Vertreter",
      confirmRemove: {
        title: "Sind Sie sich sicher?",
        cancel: "Abbrechen",
        remove: "Entfernen",
        message: (userName: string, companyName: string) => (
          <>
            Wollen Sie <strong>{userName}</strong> wirklich von{" "}
            <strong>{companyName}</strong> entfernen?
          </>
        ),
      },
    },
    editUser: {
      titleNew: "Benutzer einladen",
      titleEdit: "Benutzer bearbeiten",
      backLink: "Zurück zu Benutzer",
      namePlaceholder: "Name",
      emailPlaceholder: "E-Mail-Adresse",
      legalRepresentative: "Gesetzlicher Vertreter",
      invite: "Einladen",
      panelPermissions: "Berechtigungen",
      panelApplications: "Anwendungen",
      panelDocuments: "Dokumente",
      panelWebdesk: "Webdesk",
      webdeskUsername: "Benutzername",
      inviteSent: "Einladung erfolgreich versendet.",
      inviteError: "Fehler beim Versenden der Einladung.",
    },
  },
  documents: {
    title: "Dokumente",
    maintenanceAlert: (from: string, to: string) =>
      `Aufgrund von Wartungsarbeiten werden die Dokumente am Mittwoch, den 12. August, von ${from} bis voraussichtlich ${to} nicht einsehbar sein.`,
    loadError: "Fehler beim Laden der Dokumente.",
    pdfFallback: (documentLink: string) =>
      `Dieser Browser unterstützt keine PDF-Vorschau. Bitte laden Sie das ${documentLink} herunter, um es anzusehen.`,
    download: {
      downloadAll: "Alle Treffer herunterladen",
      formatPrompt: "Wählen Sie ein Format aus:",
      formats: {
        zip: {
          label: "ZIP",
          description:
            "Alle Dokumente einzeln in einer ZIP-Datei herunterladen",
        },
        pdf: {
          label: "PDF",
          description: "Alle Dokumente in einer PDF-Datei herunterladen",
        },
        pdfWithoutMyGastroDocx: {
          label: "PDF ohne MyGastroDocx",
          description:
            "Alle Dokumente in einer PDF-Datei ohne die Lohnstreifen MyGastroDocx herunterladen",
        },
      },
      started: "Der Download wurde gestartet.",
      success: "Die Datei wurde erfolgreich heruntergeladen.",
      error: "Fehler beim Herunterladen der Datei.",
    },
    table: {
      columnHeaders: {
        department: "Abteilung",
        memberId: "Mitgliedsnummer",
        customer: "Kunde",
        date: "Datum",
        document: "Dokument",
        employee: "Mitarbeiter",
        myGastroDocx: "MyGastroDocx",
      },
      openError: "Fehler beim Öffnen des Dokuments.",
      myGastroDocxYes: "Ja",
    },
  },
  bilanzOnline: {
    title: "Kennzahlen",
    description:
      "Hier können Sie jederzeit Ihre aktuellen Daten abrufen, analysieren, Vergleichszeiträume erstellen und Ihre Kennzahlen prüfen.",
    companySelect: "Betriebsauswahl",
    evaluation: {
      label: "Auswertung",
      options: {
        competence: "Betriebswirtschaftlich",
        fiscal: "Steuerrechtlich",
      },
    },
    periodSelect: {
      label: "Zeitraum",
      from: "Von",
      to: "Bis",
    },
    wageCostAnalysis: {
      title: "Aufstellung Lohnkosten",
      annualComparison: "Jahresübersicht",
      annualComparisonUntil: "Jahresübersicht bis",
      showWholeYear: "Zeige gesamtes Jahr",
      wageCostLabels: {
        wage: "Entlohnung",
        additionalSalaries: "Zusatzgehälter",
        otherWage: "Andere Lohnelemente",
        dues: "Beiträge",
        abfertigung: "Abfertigung (inkl. Fonds)",
        sum: "Lohnkosten",
      },
      otherWageDetail: "Prämien / Spesenvergütungen / ausbezahlte Resturlaube",
      dataAvailableUntil: (month: string) => `Daten nur bis ${month} vorhanden`,
      monthlyComparison: "Monatsübersicht",
      month: "Monat",
      columnHeaders: {
        label: "Bezeichnung",
        difference: "Abw.",
      },
    },
    wageCostCharts: {
      title: "Grafische Darstellung Lohnkosten",
      breakdownLaborCosts: "Aufstellung Lohnkosten",
      showPreviousYear: "Zeige Vorjahr",
      averageEmployeesPerMonth:
        "Durchschnittliche Anzahl Mitarbeiter pro Monat",
      employeeFilter: {
        label: "Mitarbeiter",
        options: {
          all: "Alle",
          employees: "Lohnabhängige Mitarbeiter",
          owner_and_employedfamilymembers:
            "Gesellschafter / Familienmitglieder",
        },
      },
      yearsSelect: {
        year: "Jahr",
        years: "Jahre",
      },
      period: "Zeitraum",
      workdaysPerMonth: "Arbeitstage pro Monat",
      absentDaysPerMonth: "Fehltage pro Monat",
    },
    threeYearAnalysis: {
      title: "Buchhalterische Drei-Jahres-Analyse",
      showAllRows: "Zeige alle Zeilen",
      showWholeYear: "Zeige gesamtes Jahr",
      showDifferences: "Zeige Abweichung in Prozent zum Vorjahr",
      downloadExcel: "Excel-Dokument herunterladen",
      columnHeaders: {
        sum: {
          label: "Summe",
          description: "Die Summe der Beträge",
        },
        percent: {
          label: "Prozent",
          description: "Prozentualer Anteil vom Ertrag",
        },
        yearAsSum: {
          label: "Jahr als Summe",
          description: "Summe gerechnet auf die Daten des kompletten Jahres",
        },
        yearInPercent: {
          label: "Jahr in Prozent",
          description: "Prozent gerechnet auf die Daten des kompletten Jahres",
        },
        difference: {
          label: "Abw.",
          description: "Abweichung in Prozent zum vorherigen Jahr",
        },
      },
    },
    monthlyAnalysis: {
      title: "Buchhalterische Monats-Analyse",
      showAllRows: "Zeige alle Zeilen",
      showPreviousYear: "Zeige Vorjahr",
      downloadExcel: "Excel-Dokument herunterladen",
      label: "Bezeichnung",
    },
    accountingCharts: {
      title: "Buchhalterische Kennzahlen",
      showVerwalterEntschaedigung: "Verwalterentschädigung ausblenden",
      noData: "Es wurden keine Daten für den ausgewählten Zeitraum gefunden.",
      chart1: {
        title: "Darstellung Umsatz – Wareneinsatz – Personalkosten",
        error:
          "Fehler beim Laden der Darstellung Umsatz – Wareneinsatz – Personalkosten.",
      },
      chart2: {
        title: "Betriebskostenentwicklung",
        error: "Fehler beim Laden der Betriebskostenentwicklung.",
      },
      chart3: {
        title: "Monats-Umsatz-Analyse",
        error: "Fehler beim Laden der Monats-Umsatz-Analyse.",
      },
      chart4: {
        title: "Nächtigungen",
        error: "Fehler beim Laden der Nächtigungen.",
      },
      chart5: {
        title: (chartLabel: string) =>
          `Analyse Umsatz – operative Kosten pro ${chartLabel}`,
        chartLabel: {
          overnightStays: "Nächtigungen Erwachsene",
          seating: "Sitzplätze",
          daysOpen: "Offenhaltetage",
          rentedPitches: "Vermietete Stellplätze",
        },
        selectCompany: "Bitte wählen Sie einen Betrieb aus.",
        error: "Fehler beim Laden der Analyse Umsatz – operative Kosten.",
      },
    },
  },
  agreements: {
    title: "Verträge & Preise",
    description: (
      <>
        In den nachfolgenden Tabellen finden Sie die aktuelle/n Preisliste/n
        sowie jene Verträge, welche Ihnen von der Abteilung Web Marketing zur
        Nutzung verschiedener Produkte und Dienstleistungen freigeschaltet
        wurden. Neben den offenen Verträgen können Sie hier auch bereits
        bestätigte und gekündigte Verträge einsehen.
      </>
    ),
    hint: "Hinweis",
    hintText: {
      paragraph1:
        "Bevor die Nutzungsverträge eingesehen und bestätigt werden können, muss der Rahmenvertrag gelesen und abgeschlossen werden. Dies erfolgt in telematischer Form, mit Klick auf „Bestätige jetzt“. Der Rahmenvertrag beinhaltet die allgemeinen Vertragsbedingungen, die Nutzungsverträge regeln hingegen die Nutzung der verschiedenen Produkte und dazugehörigen Leistungen. Auch diese werden mit Klick auf „Bestätige jetzt“ abgeschlossen. Die entsprechenden Kosten können in der Preisliste eingesehen werden.",
      paragraph2:
        "Im Anschluss an die Bestätigung eines Vertrages erhalten Sie eine automatische E-Mail mit der entsprechenden Vertragskopie.",
      paragraph3:
        "Alle weiteren Details finden Sie im Rahmenvertrag sowie in den entsprechenden Nutzungsverträgen.",
      paragraph4:
        "Für weitere Informationen steht Ihnen die Abteilung Web Marketing des HGV Bozen gerne zur Verfügung!",
    },
    documentsTable: {
      title: "Dokumente Web Marketing",
      privacyPolicy: "Datenschutzerklärung",
    },
    agreementsTable: {
      columnHeaders: {
        agreement: "Vertrag",
        memberOrCompany: "Mitglied / Betriebsstätte",
        confirmedAt: "Bestätigt am",
        status: "Status",
      },
      readAndConfirm: "Lesen und Bestätigen",
      confirmFrameAgreementFirst:
        "Bitte bestätigen Sie zuerst den Rahmenvertrag.",
    },
    detail: {
      backLink: "Zurück zu Verträge & Preise",
      signing: "Der Vertrag wird bestätigt …",
      retry: "Erneut versuchen",
      signError:
        "Fehler beim Bestätigen des Vertrages! Bitte versuchen Sie es erneut oder kontaktieren Sie die HGV-Abteilung Web Marketing.",
      scrollToConfirm:
        "Bitte scrollen Sie ganz nach unten, um den Vertrag zu bestätigen.",
      acceptTerms:
        "Ich nehme obige Vertragsbedingungen explizit an und bestätige die Unterzeichnung des Vertrages.",
      acceptVexatoryClauses: {
        intro:
          "Der Kunde bestätigt ausdrücklich folgende Artikel des Rahmenvertrages zu kennen und anzunehmen:",
        clauses: {
          clause1:
            "Art. 2 Laufzeit, Rücktritt und Kündigung des HGV-Rahmenvertrages;",
          clause2:
            "Art. 5 Laufzeit, Rücktritt und Kündigung der einzelnen Nutzungsverträge;",
          clause3: "Art. 6.2 Abänderung Preisliste Nutzungsverträge;",
          clause4:
            "Art. 10 Ausdrückliche Auflösungsklausel der Nutzungsverträge;",
          clause5: "Art. 11 Anwendbares Recht und Gerichtsbarkeit;",
        },
      },
      confirm: "Bestätige jetzt",
      loadingAgreement: "Lade Vertrag …",
    },
  },
  departmentApps: {
    noApps: "Es sind keine Anwendungen aktiviert.",
    noAppsContact: (emailLink: ReactNode) => (
      <>
        Falls Sie Interesse an einer unverbindlichen Beratung haben oder sich
        über unsere Produkte und Dienstleistungen näher informieren möchten,
        wenden Sie sich bitte per E-Mail an {emailLink}.
      </>
    ),
  },
  configIncomplete:
    "Die Konfiguration des Betriebes wurde noch nicht vollständig abgeschlossen.",
  openItems: {
    title: "Offene Posten",
    lastUpdate: (date: string) => `Letzte Aktualisierung: ${date}`,
    loadError: "Fehler beim Laden der offenen Posten.",
    supplier: "Lieferant",
    customer: "Kunde",
    companiesTable: {
      columnHeaders: {
        name: "Bezeichnung",
        vatId: "MwSt.-Nummer",
        balance: "Saldo",
      },
      total: "Summe",
      unassigned: "<Nicht zuordenbare Posten>",
    },
    filters: {
      status: {
        label: "Status",
        option: {
          all: "Alle",
          open: "Offen",
          settled: "Beglichen",
        },
      },
      openUntil: "Offen bis",
      search: {
        label: "Suche",
        placeholder: "Bezeichnung / MwSt.-Nummer",
      },
    },
    documentsTable: {
      columnHeaders: {
        invoiceDate: "Rechnungsdatum",
        invoiceNumber: "Rechnungs-Nr.",
        documentNumber: "Dokument-Nr.",
        bookingDate: "Buchungsdatum",
        credit: "Haben",
        debit: "Soll",
        comment: "Anmerkung",
        title: "Bezeichnung",
      },
    },
  },
  payrollBackLink: "Zurück zu Personalberatung",
  arbeitssicherheit: {
    title: "Arbeitssicherheit für Arbeitnehmer:innen",
    paragraph1:
      "Die Bestimmungen im Bereich Arbeitssicherheit sehen vor, dass Arbeitnehmende einen achtstündigen Arbeitssicherheitskurs (niedriges Risiko) absolvieren müssen. Der für die Mitarbeitenden im Hotel- und Gastgewerbe vorgesehene Kurs setzt sich aus vier Stunden allgemeiner Ausbildung und vier Stunden spezifischer Ausbildung zusammen.",
    paragraph2:
      "Alle fünf Jahre ab Kursbesuch muss ein Auffrischungskurs von sechs Stunden absolviert werden.",
    onlineCourse: {
      linkLabel: "Hier",
      text: "können Sie Ihre Mitarbeiter:innen zum Online-Kurs „Arbeitssicherheit“ anmelden.",
    },
    presenceCourse: {
      linkLabel: "Hier",
      text: "können Sie Ihre Mitarbeiter:innen zu einem der offenen Weiterbildungskurse zum Thema „Arbeitssicherheit“ in Präsenzform anmelden.",
    },
    asFrom: "Stand: August 2025",
  },
  uniUrg: {
    title: "UniUrg – dringende Anmeldungen außerhalb der Öffnungszeiten",
    paragraph1:
      "Vor Arbeitsbeginn einer untergeordneten Arbeitstätigkeit muss in Italien bekanntlich die Mitarbeiteranmeldung mittels des Formulars UniLav vorgenommen werden.",
    paragraph2: (
      <>
        Ist eine Anmeldung mittels UniLav am Vortag vor Arbeitsbeginn aus
        besonderen Dringlichkeitsumständen nicht möglich (beispielsweise, weil
        das Lohnbüro am Wochenende geschlossen ist), besteht die Möglichkeit,
        die Anmeldung eigenständig mittels UniUrg provisorisch vorzunehmen.{" "}
        <strong>
          Die ordentliche Anmeldung mittels UniLav ist daraufhin dennoch
          innerhalb von fünf Tagen nachzureichen, daher muss auch die zuständige
          HGV-Personalberatung über die erfolgte UniUrg-Meldung umgehend mittels
          E-Mail in Kenntnis gesetzt werden, wobei sämtliche Daten, welche für
          die Ausarbeitung des Arbeitsvertrages notwendig sind, ergänzt werden
          müssen.
        </strong>
      </>
    ),
    paragraph3:
      "Beschränkt auf Südtirol wird ab 4. August 2025 das Meldeverfahren abgeändert, wonach die Meldung nur noch über folgende digitale Wege möglich ist:",
    option1: "über das System ProNotel2 oder",
    option2: (link: ReactNode) => (
      <>alternativ über das Online-Formular unter: {link}</>
    ),
    paragraph4:
      "(z. B. im Falle technischer Probleme mit ProNotel2 oder bei vorübergehender Nichterreichbarkeit des Arbeitsrechtsberaters/Lohnbüros).",
    paragraph5: (email: ReactNode) => (
      <>
        <strong>
          Wichtig: Somit ist ab dem genannten Stichtag das Übermitteln der
          UniUrg-Meldungen an die E-Mail-Adresse {email} nicht mehr zulässig.
        </strong>
      </>
    ),
  },
  emergencyService: {
    title: "Individueller Notfalldienst der HGV-Personalberatung",
    introduction: "Was tun bei:",
    articles: {
      registrations: {
        title: "Anmeldungen",
        paragraph1:
          "Wir erinnern hiermit daran, dass alle Anmeldungen bis spätestens 11:45 Uhr des letzten Werktages vor Arbeitsbeginn über die Funktion Mitarbeitermeldung im HGV Serviceportal an die zuständige HGV-Personalberatung übermittelt werden müssen.",
        paragraph2: (link: ReactNode) => (
          <>
            Sollte dennoch am Wochenende oder an Feiertagen eine dringende
            Mitarbeiteranmeldung nötig sein, können Sie die
            Mitarbeiteranmeldung,{" "}
            <strong>
              innerhalb 24 Uhr des Tages vor Arbeitsbeginn, über das {link}
            </strong>{" "}
            direkt ans Arbeitsamt übermitteln.
          </>
        ),
        paragraph2LinkLabel: "Online-Formular",
        paragraph3: (email: ReactNode) => (
          <>
            Die Versandbestätigung des Formulars UniUrg sowie die entsprechende
            Meldungsnummer, die vom Amt nach erfolgter Übermittlung zugewiesen
            wird, sind vom Kunden unverzüglich an den:die jeweilige:n
            Ansprechpartner:in der HGV-Personalberatung weiterzuleiten ({email}
            ), damit die ordentliche UniLav-Anmeldung sowie die Ausarbeitung des
            Arbeitsvertrages am erstmöglichen Tag erfolgen können.
          </>
        ),
      },
      disputes: {
        title: "Streitfälle/Disziplinarmaßnahmen",
        paragraph1: (
          <>
            Falls es am Wochenende oder an einem Feiertag zu einer gravierenden
            Streitigkeit (Handgreiflichkeit/Beleidigung/Trunkenheit) kommen
            sollte, empfehlen wir Ihnen, den:die <strong>Mitarbeitenden</strong>{" "}
            für die Tage der Schließung der HGV-Personalberatung{" "}
            <strong>vom Dienst zu befreien</strong> (Urlaub,
            Überstundenausgleich o. Ä.).
          </>
        ),
        paragraph2: (
          <>
            Kontaktieren Sie bitte <strong>am ersten Werktag</strong> Ihre:n
            Ansprechpartner:in der HGV-Personalberatung, um den Sachverhalt zu
            besprechen und das weitere Vorgehen gemeinsam zu bestimmen.
          </>
        ),
      },
      absence: {
        title: "Unentschuldigtes Fernbleiben vom Arbeitsplatz",
        paragraph1:
          "Falls ein Mitarbeiter ohne triftigen Grund (Krankschreibung, Unfall usw.) bzw. Erlaubnis (Urlaub) seinem Arbeitsplatz fernbleibt, so befindet sich der Arbeitgeber meist in einer Notsituation, welche es kurzfristig zu überbrücken gilt. Die Aufgaben und der Tätigkeitsbereich des abwesenden Mitarbeiters müssen auf die anderen Mitarbeitenden aufgeteilt werden oder aber auch eventuelle Aushilfen müssen kurzfristig gefunden und auch angemeldet (am Wochenende mittels des Formulars UniUrg) werden.",
        paragraph2:
          "Bei einer unentschuldigten Abwesenheit eines Mitarbeitenden empfehlen wir Ihnen, sich umgehend mit Ihrer:m Ansprechpartner:in der HGV-Personalberatung in Verbindung zu setzen, um das weitere Vorgehen gemeinsam abzustimmen. Bitte achten Sie bei der Kommunikation mit den betreffenden Mitarbeitenden immer auf eine sorgfältige und neutrale Wortwahl, vermeiden Sie die eigenmächtige Verfassung von Vorhaltungen oder sonstigem Schriftverkehr (beispielsweise WhatsApp oder E-Mail) und gehen Sie auf eventuelle Provokationen nicht ein, da sich dies bei einem Streitfall negativ auswirken könnte.",
        paragraph3:
          "Die unentschuldigte Abwesenheit des Mitarbeitenden muss in jedem Fall schriftlich vorgehalten werden. Da es bei der Vorhaltung einige Punkte zu beachten gibt, damit diese rechtmäßig und korrekt ist, wird Ihr:e Ansprechpartner:in diese individuell ausarbeiten und Ihnen zukommen lassen. Damit eine Vorhaltung auch regelkonform zugestellt werden kann, ist die korrekte Wohnadresse des Empfängers (auch für Mitarbeitende aus dem Ausland) zwingend notwendig. Durch die schriftliche Vorhaltung wird das unentschuldigte Fernbleiben belegt und dem Mitarbeitenden die Möglichkeit einer Rechtfertigung – ebenfalls in schriftlicher Form – eingeräumt.",
        paragraph4:
          "Wenn die Abwesenheit nicht gerechtfertigt werden kann, wird dem Mitarbeitenden das Folgeschreiben zugestellt, in welchem er über die verhängten Disziplinarmaßnahmen informiert wird.",
        paragraph5:
          "Der Zeitraum der unentschuldigten Abwesenheit wird nicht entlohnt, es besteht weder Anspruch auf den normalen Tageslohn noch auf andere Lohnelemente. Es empfiehlt sich, den Mitarbeiter auch darüber in schriftlicher Form zu informieren.",
        paragraph6:
          "Sollte die unentschuldigte Abwesenheit des Mitarbeitenden länger als 5 Tage andauern, so besteht die Möglichkeit, das Arbeitsverhältnis zu beenden.",
        paragraph7:
          "Auch in diesem Fall muss die Abwesenheit vorab durch eine disziplinarrechtliche Vorhaltung beanstandet werden und erst nach der Feststellung, dass keine Rechtfertigungen vorliegen, kann das 2. disziplinarrechtliche Folgeschreiben mit der Entlassung übermittelt werden.",
        paragraph8:
          "Falls ein Mitarbeitender am Wochenende nicht zur Arbeit kommt, kann der Kontakt mit Ihrem zuständigen Ansprechpartner ohne Weiteres wieder am darauffolgenden Werktag zur Besprechung der Sachlage aufgenommen werden.",
      },
      workAccident: {
        title: "Arbeitsunfall des Mitarbeitenden",
        paragraph: (
          <>
            Falls ein Mitarbeitender einen Arbeitsunfall erleidet, daraufhin ins
            Krankenhaus eingeliefert wird und den entsprechenden
            INAIL-Unfallschein vorweist, darf der Mitarbeiter für den
            angegebenen Zeitraum nicht mehr seine Arbeitsleistung erbringen.
            Bitte leiten Sie den INAIL-Unfallschein umgehend an Ihren
            zuständigen Ansprechpartner der HGV-Personalberatung weiter. Die
            Meldung des Arbeitsunfalles wird dann von uns innerhalb der
            vorgesehenen Frist veranlasst. Selbst wenn der Unfall am Wochenende
            oder an Feiertagen vorfällt,{" "}
            <strong>
              kann die Unfallmeldung am nächsten Werktag eingereicht
            </strong>{" "}
            werden.
          </>
        ),
        certificateAltText: "Unfallschein",
      },
      illness: {
        title: "Krankheit des Mitarbeitenden",
        paragraph1:
          "Falls ein Mitarbeitender einen Krankenschein übermittelt bzw. vorbeibringt, darf der Mitarbeiter im angegebenen Zeitraum keine Arbeitsleistung erbringen.",
        paragraph2:
          "Bitte übermitteln Sie die Protokollnummer des Krankenscheins oder den Krankenschein selbst Ihrer:m Ansprechpartner:in der HGV-Personalberatung.",
        paragraph3:
          "Wir werden den Krankheitszeitraum dann im Stundenregister des Mitarbeitenden eintragen.",
        paragraph4:
          "Falls Ihnen der Mitarbeitende nur die Protokollnummer des Krankenscheins übermittelt und Sie somit nicht den genauen Zeitraum des Krankenstandes kennen, können Sie unter folgendem Link, durch Eingabe der Steuernummer und der Protokollnummer, den vollständigen Krankenschein abrufen.",
        inpsLinkLabel: "Attestati malattia – Consultazione (inps.it)",
        sickNoteAltText: "Krankenschein",
      },
      individualService: {
        title: "Individueller Notfalldienst",
        paragraph1: (
          <>
            Falls die oben angegebenen Fragen und Antworten Ihren konkreten
            Problemfall nicht lösen und Sie dennoch eine dringende Auskunft
            benötigen, können Sie unten auf <strong>Notfall melden</strong>{" "}
            klicken.
          </>
        ),
        paragraph2:
          "Dadurch werden Sie zeitnah vom/von der Bereichsleiter:in Ihrer zuständigen HGV-Personalberatung kontaktiert.",
        paragraph3: "Der Dienst ist in folgenden Zeiten aktiv:",
        summerHours:
          "Während der Zeit des Sommerstundenplans vom 6. Juli 2026 bis 28. August 2026 von Montag bis Freitag von 14:00 bis 17:00 Uhr.",
        holidayHoursHeading: "An den folgenden Tagen von 08:00 bis 17:00 Uhr:",
        holidays: [
          "Ostermontag, 6. April 2026",
          "Tag der Arbeit, 1. Mai 2026",
          "Pfingstmontag, 25. Mai 2026",
          "Weihnachten, 25. Dezember 2026",
        ],
        warning:
          "ACHTUNG: An den Feiertagen fällt für die Aktivierung des individuellen Notfalldienstes eine Bearbeitungsgebühr von 130,00 Euro an.",
      },
    },
    reportEmergency: "Notfall melden",
    serviceMomentarilyNotActive: "Der Notfalldienst ist zurzeit nicht aktiv.",
    initError: "Fehler beim Initialisieren der Notfallmeldung.",
    modal: {
      submit: "Notruf absetzen",
      cancel: "Abbrechen",
      company: "Betrieb",
      phone: "Telefonnummer",
      description: "Kurze Beschreibung des Vorfalles/Anliegens",
      acceptTerms:
        "Ich erkläre mich mit der Aktivierung des individuellen Notfalldienstes einverstanden, für welchen an den Feiertagen eine Bearbeitungsgebühr von 130,00 Euro berechnet wird.",
      acceptTermsRequired:
        "Bitte kreuzen Sie dieses Feld an, wenn Sie fortfahren möchten.",
      success: "Notruf erfolgreich abgesetzt.",
      error: "Fehler beim Absetzen des Notrufs.",
    },
  },
  arbeitAufAbruf: {
    title: "Arbeit auf Abruf – Vorabmeldung des Abrufs",
    paragraph1: (
      formLink: ReactNode,
      email: ReactNode,
      instructionsLink: ReactNode,
    ) => (
      <>
        Für jede:n Beschäftigte:n, welcher mit dem Vertrag „Arbeit auf Abruf“
        eingestellt ist, muss jeweils vor Arbeitsbeginn eine zusätzliche
        Mitteilung an das Arbeitsministerium erfolgen. Diese muss vom
        Arbeitgeber selbst mittels {formLink} als XML-File an die E-Mail-Adresse{" "}
        {email} geschickt werden. Die Anleitung dafür finden Sie{" "}
        {instructionsLink}.
      </>
    ),
    paragraph1FormLinkLabel: "Online-Formular „UNI_INTERMITTENTE“",
    paragraph1InstructionsLinkLabel: "hier",
    paragraph2:
      "Beispiel für die zusätzliche Meldung: Der Arbeitsvertrag auf Abruf wurde am 1. August abgeschlossen und der:die Mitarbeiter:in scheint beim Arbeitsamt als regulär gemeldet auf. Am 22. August wird der:die Mitarbeiter:in ab 11:00 Uhr und am 28. August ab 19:00 Uhr gerufen. In diesem Fall muss jeder einzelne Tag mit dem Online-Formular gemeldet werden.",
    clarification: {
      label: "Klarstellung",
      description:
        "Die Mitteilung, wann der:die Mitarbeiter:in seine Arbeit antritt, muss unmittelbar vor Arbeitsbeginn erfolgen. Ist z. B. der Arbeitsbeginn um 11:00 Uhr, muss die Mitteilung innerhalb 10:59 Uhr erfolgen.",
    },
    cancellation: {
      label: "Annullierung",
      description:
        "Eine Annullierung, weil der:die Mitarbeiter:in kurzfristig abgesagt hat und die Mitteilung bereits versendet wurde, muss ebenfalls unmittelbar am selben Arbeitstag (nicht erst am Folgetag) vorgenommen werden.",
    },
    penalties: {
      label: "Strafen bei Unterlassung",
      description:
        "Für jede nicht erfolgte Mitteilung, wann der:die Mitarbeiter:in seine Arbeit antritt bzw. nicht antritt, sieht das Gesetz Strafen in Höhe von 400 Euro bis 2.400 Euro vor.",
    },
    presenceRegister: {
      label: "Präsenzregister",
      description:
        "Die effektiv gearbeiteten Stunden müssen vom Arbeitgeber monatlich im Stundenregister eingetragen werden.",
    },
  },
  joinJobOffers: {
    title: "Leitfaden: Stellenanzeige anlegen auf onboard",
    sidebarScreenshotAltText:
      "Screenshot der Seitenleiste „Recruiting“ mit dem Menüpunkt „Stellenanzeigen“ und ausgewählter Option „Neu“",
    formScreenshotAltText:
      "Screenshot des Formulars „Basisinformationen“ mit Hinweis „Auf Weltkugel klicken“",
    steps: {
      new: "Bei der Kategorie links „Stellenanzeigen“ auf „Neu“ drücken.",
      basicInfo: {
        label: <strong>Basisinformation:</strong>,
        items: [
          "Sprachen: „alle“ auswählen, damit die Stellenanzeige auf der Webseite in allen 3 Sprachen wiedergegeben werden kann.",
          "Referenznummer: dies ist nur intern ersichtlich – falls ihr eure Stellenanzeigen sortieren wollt (z. B. ihr nummeriert alle Stellen, oder ihr verseht sie mit einem speziellen Buchstaben).",
          "Standort: euren Standort auswählen.",
          "Abteilungen: welchen Bereich des Betriebs betrifft die offene Stelle? (Küche, Service, …)",
          "Titel: z. B. Chef de Rang, Lehrling Küche → WICHTIG: auf die Weltkugel klicken, dann auf „alle übersetzen“, damit der Titel der Stellenanzeige automatisch in alle Sprachen übersetzt wird.",
        ],
      },
      content: {
        label: (
          <>
            <strong>Inhalt der Stellenanzeige</strong> (am Musterschema
            orientieren, unter „Entwürfe“ zu finden) → WICHTIG: auch hier auf
            die Weltkugel klicken, dann auf „alle übersetzen“:
          </>
        ),
        items: [
          "Kurze Einleitung, um welche Stelle es sich handelt.",
          "Was sollte die Person mitbringen = welche Kompetenzen sollte sie haben.",
          "Tätigkeitsbeschreibung: was genau ist zu tun.",
          "WICHTIG: Die übermittelte Ansprechperson des Betriebes wird bei jeder Stellenanzeige automatisch angezeigt, hier könnte man z. B. die Webseite verlinken oder nochmals einen allgemeinen Betriebskontakt hinzufügen.",
        ],
        note: "Allgemeiner Hinweis: genügend Absätze zwischen den Inhalten einplanen.",
      },
      employmentType:
        "Anstellungsverhältnis: entsprechend auswählen + Eintrittsdatum auswählen.",
      salary:
        "Gehaltsangabe machen (es kann auch eine Gehaltsspanne angegeben werden) – optional.",
      autoReply:
        "Automatische E-Mail-Antwort: bei der Vorauswahl „Standard-Antwort“ lassen.",
      beforePublishing: {
        label: "Kurz vor dem Veröffentlichen:",
        items: [
          "Wählen, ob die Stellenanzeige sofort veröffentlicht werden soll.",
          "Die Laufzeit nicht auf unbegrenzt lassen, da sonst viele veraltete Stellenanzeigen im System sind = besser auf zwei Wochen begrenzen und falls nötig verlängern.",
        ],
      },
    },
    duplicateHint:
      "Für alle künftigen Stellenanzeigen: Wenn eine Stellenanzeige nach dem richtigen Muster erstellt wurde, die Stelle einfach „duplizieren“.",
  },
  dac7: {
    backLink: "Zurück zu Web Marketing",
    notReportable: "Das gewählte Unternehmen ist nicht meldepflichtig",
    requiredOrDash:
      "Das Feld muss einen Wert beinhalten. Ist kein Wert vorhanden, tragen Sie bitte ein Minuszeichen (-) ein.",
    seller: {
      title: "Angaben zum Verkäufer",
      classification: {
        label: "Steuerliche Klassifikation",
        helpText:
          "Sollte diese Information nicht aktuell sein, so bitten wir Sie, uns zu kontaktieren.",
        company: "Unternehmen",
        soleProprietorship: "Einzelunternehmen",
        privatePerson: "Privatperson",
      },
      companyName: {
        label: "Juristischer Firmenname",
        helpText:
          "Dies ist die rechtsgültige Bezeichnung Ihres Unternehmens. Sollte diese nicht aktuell sein, so bitten wir Sie, uns zu kontaktieren.",
      },
      firstName: {
        label: "Vorname",
        helpText:
          "Dies ist Ihr rechtsgültiger Vorname. Sollte dieser nicht korrekt sein, so bitten wir Sie, uns zu kontaktieren.",
      },
      lastName: {
        label: "Nachname",
        helpText:
          "Dies ist Ihr rechtsgültiger Nachname. Sollte dieser nicht korrekt sein, so bitten wir Sie, uns zu kontaktieren.",
      },
      birthDate: "Geburtsdatum",
      pec: "PEC-Adresse",
      taxId: {
        label: "Steuernummer",
        helpText:
          "Geben Sie Ihre Steuernummer und das Land, von dem sie ausgestellt wurde, an.",
      },
      vatId: {
        label: "MwSt.-Nummer",
        helpText:
          "Geben Sie Ihre MwSt.-Nummer inklusive zweistelligem Länderkürzel an.",
      },
      reaNumber: {
        label: "VWV-Nummer (REA-Nummer)",
        helpText: (link: ReactNode) => (
          <>
            Die VWV-Nummer finden Sie auf der ersten Seite des
            Handelskammerauszuges. {link} ersichtlich anhand des roten Pfeils.
          </>
        ),
        helpTextLinkLabel: "Hier im Beispiel",
        instructionsPdfTitle: "Anleitung VWV-Nummer",
      },
      address: "Adresse",
      street: "Straße",
      buildingNumber: "Nummer",
      postalCode: "Postleitzahl",
      municipality: "Gemeinde",
      province: "Provinz",
      country: "Land",
      bankData: "Bankdaten",
      accountOwner: "Kontoinhaber",
      iban: "IBAN",
      permanentEstablishments: {
        title: "Niederlassungen",
        description:
          "Wählen Sie jene Länder aus, in denen Sie Betriebsstätten (innerhalb der Europäischen Union) vermieten.",
        minOneCountry: "Wählen Sie mindestens ein Land aus.",
      },
    },
    companies: {
      title: "Angaben zu den Betriebsstätten",
      description:
        "Geben Sie für jede Liegenschaftseinheit, die über das Portal „Booking Südtirol“ vermietet wird, jeweils folgende Daten an.",
      notReportable: "Diese Betriebsstätte ist nicht meldepflichtig",
      address: "Adresse",
      street: "Straße",
      buildingNumber: "Nummer",
      postalCode: "Postleitzahl",
      municipality: "Gemeinde",
      province: "Provinz",
      country: "Land",
      landRegistration: {
        title: "Katasterdaten",
        description: (link: ReactNode) => (
          <>
            {link} finden Sie die Anleitung, wie die abgefragten Daten aus dem
            Katasterauszug der jeweiligen Liegenschaftseinheit ausgelesen werden
            können. Ist kein Wert für Blatt oder Baueinheit vorhanden, tragen
            Sie bitte ein Minuszeichen (-) in das entsprechende Feld ein.
          </>
        ),
        descriptionLinkLabel: "In diesem Dokument",
        instructionsPdfTitle: "Angabe der Katasterdaten für die Meldung DAC7",
        municipalityCode: {
          title: "Kodex der Gemeinde",
          subtitle: "Codice catastale del comune",
        },
        sheet: { title: "Blatt", subtitle: "Foglio" },
        parcel: { title: "Parzelle", subtitle: "Particella" },
        subunit: { title: "Baueinheit", subtitle: "Sub. oder subalterno" },
        addEntry: "Eintrag hinzufügen",
      },
    },
    errors: {
      loadError: "Fehler beim Laden der Mitgliedsdaten.",
      configIncomplete: {
        line1:
          "Die Konfiguration des gewählten Mitglieds wurde noch nicht vollständig abgeschlossen.",
        line2: "Bitte wenden Sie sich an die HGV-Abteilung Web Marketing.",
      },
    },
  },
};

export type I18N = typeof i18n;

export default i18n;
